import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface Props {
  /** Match date in YYYY-MM-DD form */
  date: string;
  /** Kick-off time (HH:MM[:SS]) — countdown is skipped when null */
  matchTime?: string | null;
  className?: string;
}

/** Builds the exact kick-off Date from the stored date + time columns. */
export function kickoffDate(date: string, matchTime?: string | null): Date | null {
  if (!matchTime || !date) return null;
  const [y, mo, dd] = String(date).slice(0, 10).split("-").map(Number);
  if (!y || !mo || !dd) return null;
  const [h, m] = String(matchTime).split(":").map(Number);
  // Anchor to local time — `new Date("YYYY-MM-DD")` would parse as UTC midnight.
  return new Date(y, mo - 1, dd, h || 0, m || 0, 0, 0);
}

/**
 * Small "T-minus" chip for the next upcoming match.
 * Ticks once a minute; renders nothing once the match window has passed
 * or when no kick-off time is known.
 */
export function KickoffCountdown({ date, matchTime, className }: Props) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const kickoff = kickoffDate(date, matchTime);
  if (!kickoff) return null;

  const diffMs = kickoff.getTime() - now;
  const MATCH_WINDOW_MS = 2 * 60 * 60 * 1000;

  // In-play window: kick-off to +2h
  if (diffMs <= 0) {
    if (diffMs < -MATCH_WINDOW_MS) return null;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border border-green-500/40 bg-green-500/10 px-2 py-0.5 text-[11px] font-medium text-green-600 dark:text-green-400",
          className
        )}
      >
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
        Live
      </span>
    );
  }

  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let label: string;
  if (days >= 1) {
    label = `in ${days} day${days === 1 ? "" : "s"}`;
  } else if (hours >= 1) {
    label = `in ${hours}h ${minutes % 60}m`;
  } else {
    label = `in ${Math.max(1, minutes)}m`;
  }

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary",
        className
      )}
      title={`Kick-off ${kickoff.toLocaleString()}`}
    >
      {label}
    </span>
  );
}
